import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { auth } from '../firebase';
import axios from 'axios';
import { toast } from 'sonner';
import { Scissors } from 'lucide-react';

const API = '/api';

/**
 * Phase 1 — App-style single OTP login.
 * One login for everyone (customer + salon). After verify → /app/home.
 * Salon onboarding happens later from inside the app (profile → List Your Salon).
 */
export default function AppLogin() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // phone | otp
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    if (timer <= 0) return;
    const t = setTimeout(() => setTimer(timer - 1), 1000);
    return () => clearTimeout(t);
  }, [timer]);

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
      });
    }
    return window.recaptchaVerifier;
  };

  const sendOTP = async () => {
    if (!phone || phone.length !== 10) {
      toast.error('Sahi 10-digit number daalein');
      return;
    }
    setLoading(true);
    try {
      const appVerifier = setupRecaptcha();
      const result = await signInWithPhoneNumber(auth, `+91${phone}`, appVerifier);
      setConfirmationResult(result);
      setStep('otp');
      setTimer(60);
      toast.success(`OTP bheja gaya +91 ${phone} par`);
    } catch (error) {
      console.error('OTP error:', error);
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (e) {}
        window.recaptchaVerifier = null;
      }
      if (error.code === 'auth/too-many-requests') toast.error('Bahut baar try kiya. Thodi der baad karein.');
      else if (error.code === 'auth/invalid-phone-number') toast.error('Galat number. Dobara check karein.');
      else toast.error('OTP nahi bhej paye. Dobara try karein.');
    }
    setLoading(false);
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('6-digit OTP daalein');
      return;
    }
    if (!confirmationResult) {
      toast.error('Pehle OTP mangwाyein');
      return;
    }
    setLoading(true);
    try {
      const result = await confirmationResult.confirm(otp);
      const user = result.user;
      localStorage.setItem('userRole', 'customer');
      localStorage.setItem('userPhone', phone);
      localStorage.setItem('firebaseUid', user.uid);
      try {
        await axios.post(`${API}/auth/track-login`, {
          phone, firebase_uid: user.uid, login_method: 'otp',
        });
      } catch (e) { /* non-critical */ }
      toast.success('Welcome! 🎉');
      navigate('/app/home', { replace: true });
    } catch (error) {
      console.error('Verify error:', error);
      toast.error('Galat OTP. Dobara try karein.');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#1c1c28', color: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', position: 'relative', overflow: 'hidden' }}>
      <div id="recaptcha-container"></div>
      {/* background */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg,#b91d47,#5e0f26)' }}>
        <div style={{ position: 'absolute', fontSize: 80, opacity: 0.08, top: 60, left: 20, lineHeight: 2, letterSpacing: 20 }}>💈✂️💇</div>
      </div>

      {/* sheet */}
      <div style={{ position: 'relative', background: '#fff', color: '#1c1c28', borderRadius: '28px 28px 0 0', padding: '30px 26px 40px' }}>
        <div style={{ position: 'absolute', top: -130, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 84, height: 84, borderRadius: 24, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 30px rgba(0,0,0,.25)', position: 'relative' }}>
            <Scissors size={44} color="#b91d47" strokeWidth={2.5} />
            <div style={{ position: 'absolute', bottom: 6, right: 6, width: 14, height: 14, background: '#ffb800', borderRadius: '50%', border: '2px solid #fff' }} />
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: '#fff' }}>BookYourSalons</div>
        </div>

        {step === 'phone' ? (
          <>
            <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em' }}>Welcome 👋</h1>
            <p style={{ color: '#7a7a8c', marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>Ghar baithe ya salon jaake — book karein seconds me. Phone se login karein.</p>
            <div style={{ marginTop: 22, background: '#fdf2f5', border: '1.5px solid #ffe0e0', borderRadius: 14, padding: '15px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>+91</span>
              <input
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="98765 43210"
                inputMode="numeric"
                style={{ background: 'none', border: 'none', outline: 'none', fontSize: 17, flex: 1, letterSpacing: 1 }}
              />
            </div>
            <button onClick={sendOTP} disabled={loading}
              style={{ background: 'linear-gradient(135deg,#b91d47,#8f1538)', color: '#fff', border: 'none', borderRadius: 14, padding: 16, fontSize: 16, fontWeight: 700, width: '100%', marginTop: 16, cursor: 'pointer', boxShadow: '0 8px 20px rgba(185,29,71,.3)', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Bhej rahe hain...' : 'Send OTP →'}
            </button>
            <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#7a7a8c' }}>Naya user? Bas OTP se seedha login ho jayega.</p>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em' }}>Verify OTP</h1>
            <p style={{ color: '#7a7a8c', marginTop: 8, fontSize: 14 }}>+91 {phone} par code bheja gaya.</p>
            <input
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit OTP"
              inputMode="numeric"
              style={{ marginTop: 22, background: '#fdf2f5', border: '1.5px solid #ffe0e0', borderRadius: 14, padding: '16px', fontSize: 22, fontWeight: 800, width: '100%', textAlign: 'center', letterSpacing: 8, outline: 'none' }}
            />
            <button onClick={verifyOTP} disabled={loading}
              style={{ background: 'linear-gradient(135deg,#b91d47,#8f1538)', color: '#fff', border: 'none', borderRadius: 14, padding: 16, fontSize: 16, fontWeight: 700, width: '100%', marginTop: 16, cursor: 'pointer', boxShadow: '0 8px 20px rgba(185,29,71,.3)', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Check kar rahe hain...' : 'Verify & Continue →'}
            </button>
            <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#7a7a8c' }}>
              {timer > 0 ? `Resend OTP in 0:${String(timer).padStart(2, '0')}` : <span onClick={sendOTP} style={{ color: '#b91d47', fontWeight: 700, cursor: 'pointer' }}>Resend OTP</span>}
            </p>
            <p onClick={() => { setStep('phone'); setOtp(''); }} style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: '#7a7a8c', cursor: 'pointer' }}>← Number badlein</p>
          </>
        )}
      </div>
    </div>
  );
}
